/**
 * The workspace floating chat window body: the shared FloatingChat header
 * (session switcher, new chat, open-as-tab, size toggle, minimize) plus the
 * shared chat stack (see `components/FloatingChat/` — the canvas twin,
 * `CanvasSessionPane`, composes the same pieces).
 *
 * The chat body mirrors `WorkspaceChatPage`'s `WorkspaceChatBody` — the same
 * `ChatArea` + always-mounted `MessageInput` under `SessionProvider` /
 * `MessageInputProvider` — so a chat reads identically whether it lives in this
 * floating window or a full `/chat/:id` tab.
 *
 * Draft mode (`flowSessionId == null`): the composer has no session yet and
 * mints one on the first send (via `useMessageActions`), then commits it to the
 * floating stack. The session is committed on *send*, not on create, so
 * attaching a file in a draft (which creates the session early) doesn't remount
 * the body and drop the upload queue.
 */

import type { MessageInputMethod } from "@/analytics/surfaces";
import { useDeleteFlowSession, useFlowSessionById } from "@/api/flow_sessions";
import { useFlowMessages } from "@/api/messages";
import { FloatingChatBody } from "@/components/FloatingChat/FloatingChatBody";
import {
  FloatingChatEmptyState,
  type FloatingChatSuggestedAction,
} from "@/components/FloatingChat/FloatingChatEmptyState";
import { FloatingChatHeader } from "@/components/FloatingChat/FloatingChatHeader";
import { FloatingChatPaneShell } from "@/components/FloatingChat/FloatingChatPaneShell";
import { MessageInput } from "@/components/MessageInput";
import { usePermissionModeControl } from "@/components/MessageInput/hooks/usePermissionModeControl";
import { ReferencedEntitiesPills } from "@/components/ReferencedEntitiesPills";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MessageInputControlSet } from "@/constants/message-input";
import type { WorkspaceFloatingSessionHistory } from "@/context/WorkspaceFloatingChatContext";
import { ComposerAttachDropTarget } from "@/dnd/ComposerAttachDropTarget";
import { useOnDrop } from "@/dnd/OsFileDropLayer";
import { useRegisterComposerAttachTarget } from "@/dnd/TransferDndProvider";
import { useChatPresence } from "@/hooks/useChatPresence";
import { useMessageActions } from "@/hooks/useMessageActions";
import Sentry from "@/integrations/Sentry";
import { useReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { useFileRecordTextSelectionReferencedEntity } from "@/panes/file-record-detail/useFileRecordTextSelectionReferencedEntity";
import type { InboxItem, PostMessageParams } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import {
  ExternalLink,
  FileText,
  HelpCircle,
  Lightbulb,
  SearchIcon,
} from "lucide-react";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";

/**
 * Registry/droppable key for the floating window's `composer-attach` surface.
 * Distinct from the page composer's plain `workspaceId ?? "home"` key so the
 * two simultaneously-mounted targets never collide.
 */
function floatingChatScopeKey(scopeWorkspaceId: string | undefined): string {
  return `floating:${scopeWorkspaceId ?? "home"}`;
}

const SUGGESTED_ACTIONS: FloatingChatSuggestedAction[] = [
  {
    id: "summarize-files",
    label: "Summarize my files",
    message: "Summarize the files in this workspace for me.",
    icon: SearchIcon,
  },
  {
    id: "brainstorm",
    label: "Brainstorm ideas",
    message: "Help me brainstorm ideas for a new project in this workspace.",
    icon: Lightbulb,
  },
  {
    id: "create-doc",
    label: "Create a document",
    message: "Create a new document for me.",
    icon: FileText,
  },
  {
    id: "help",
    label: "What can you do?",
    message: "What are all the things you can help me with here?",
    icon: HelpCircle,
  },
];

export interface WorkspaceFloatingChatPaneProps {
  /** The session shown, or null when the window is a draft. */
  flowSessionId: string | null;
  /** Workspace new sessions (draft / new chat) are created in. */
  scopeWorkspaceId: string | undefined;
  /** Sessions listed in the header switcher (scope-resolved by the panel). */
  sessions: InboxItem[];
  canGoBack: boolean;
  onGoBack: () => void;
  /** Minimize to the FAB. */
  onClose: () => void;
  /** True in the full-height (docked) size class — drives the expand-toggle icon. */
  isChatExpanded: boolean;
  onToggleExpand: () => void;
  onOpenSession: (
    sessionId: string,
    history?: WorkspaceFloatingSessionHistory,
  ) => void;
  /** Start a fresh draft (empty composer). */
  onNewChat: () => void;
  /** Promote the current session into a full-page `/chat/:id` tab. */
  onOpenAsTab: () => void;
}

/** Chat body — shares the `SessionProvider` scope so the composer can send. */
function WorkspaceFloatingChatBody({
  flowSessionId,
  scopeWorkspaceId,
  onCommitSession,
}: {
  flowSessionId: string | null;
  scopeWorkspaceId: string | undefined;
  /** Called with the minted session id after the first send in a draft. */
  onCommitSession: (sessionId: string) => void;
}) {
  const isDraft = flowSessionId == null;

  const {
    onSendMessage: onSendMessageBase,
    isLoading,
    uploadedFiles,
    onAttachFile,
    onAttachFileRecords,
    onRemoveFile,
    ensureFlowSession,
  } = useMessageActions({
    surface: "floating_chat",
    workspaceId: scopeWorkspaceId,
    // Commit only from the draft instance's first send — the existing-session
    // instance already has its id on the stack.
    onMessageSent: ({ flowSession }) => {
      if (isDraft) onCommitSession(flowSession.id);
    },
  });

  // Report presence while a session is up so tool confirmations surface here
  // instead of the notification bell (the pane unmounts on minimize, so a
  // collapsed chat correctly reports no presence).
  useChatPresence(flowSessionId ?? undefined);

  // In-app dnd-kit drags land on the `composer-attach` surface wrapping the
  // window (registered under the pane's own scope key — the hero composer on
  // the page keeps its separate registration). A draft mints its session on
  // drop, exactly like the attach button, WITHOUT committing it to the stack —
  // committing happens on first send so the keyed body doesn't remount and
  // drop the upload queue mid-attach.
  useRegisterComposerAttachTarget(floatingChatScopeKey(scopeWorkspaceId), {
    ensureSession: isDraft ? ensureFlowSession : async () => flowSessionId,
    onAttached: (records) =>
      onAttachFileRecords(records, { method: "drop_in_app" }),
  });

  // Text excerpts pushed from a right-pane file-record viewer ride along on the
  // next message, scoped to this chat (mirrors the full chat page + canvas).
  const { textSelectionEntities, removeSelection, clearSelectionsFromMessage } =
    useFileRecordTextSelectionReferencedEntity(flowSessionId ?? "");

  const onSendMessage = useCallback(
    async (
      params: PostMessageParams,
      meta?: { inputMethod: MessageInputMethod },
    ) => {
      await onSendMessageBase(params, meta);
      clearSelectionsFromMessage(params.referenced_entities);
    },
    [onSendMessageBase, clearSelectionsFromMessage],
  );

  const onOsFileDrop = useCallback(
    (files: File[] | null) => onAttachFile(files, { method: "drop_os" }),
    [onAttachFile],
  );
  useOnDrop(onOsFileDrop);

  const { data: messages = [], isPending: isMessagesPending } = useFlowMessages(
    flowSessionId ?? "",
    undefined,
    { enabled: !!flowSessionId },
  );

  const [reasoningEffort, setReasoningEffort] = useReasoningEffort(
    `chat_reasoning_effort_${flowSessionId ?? "draft"}`,
  );

  const hasMessages = messages.length > 0;

  // Shares the composer's pending pick, so suggested actions stamp it too.
  const permissionModeControl = usePermissionModeControl({
    flowSessionId: flowSessionId ?? undefined,
  });

  const handleSuggestedAction = useCallback(
    (action: FloatingChatSuggestedAction) => {
      onSendMessage(
        {
          id: uuidv4(),
          content: action.message,
          referenced_entities: textSelectionEntities,
          permission_mode: permissionModeControl.sendPermissionMode,
        } as PostMessageParams,
        { inputMethod: "suggested_action" },
      );
    },
    [
      onSendMessage,
      textSelectionEntities,
      permissionModeControl.sendPermissionMode,
    ],
  );

  return (
    <FloatingChatBody
      surface="floating_chat"
      isMessagesPending={isMessagesPending}
      hasMessages={hasMessages}
      controlSet={MessageInputControlSet.WORKSPACE_CHAT}
      placeholder="Ask Cohesive anything…"
      referencedEntities={textSelectionEntities}
      onSendSuccess={clearSelectionsFromMessage}
      emptyState={
        <FloatingChatEmptyState
          hint="Mention a file with @, or run a Skill with the spark."
          actions={SUGGESTED_ACTIONS}
          onAction={handleSuggestedAction}
        />
      }
    >
      <MessageInput
        flowSessionId={flowSessionId ?? undefined}
        onSend={onSendMessage}
        attachedFiles={uploadedFiles}
        onAttachFile={onAttachFile}
        onAttachFileRecords={onAttachFileRecords}
        // The add-menu's file picker searches this chat's workspace tree.
        filesWorkspaceId={scopeWorkspaceId}
        onRemoveFile={onRemoveFile}
        sendButtonDisabled={isLoading}
        controlSet={MessageInputControlSet.WORKSPACE_CHAT}
        location="sidebar"
        placeholder="Ask Cohesive anything…"
        // The window is an explicit chat surface — take focus on open, unlike
        // the canvas pane (which only focuses an empty session).
        autoFocus
        agentReasoningEffort={reasoningEffort}
        onAgentReasoningEffortChange={setReasoningEffort}
        referencedEntities={textSelectionEntities}
        className="shrink-0 px-3 pb-3"
        middleToolbarSlot={
          textSelectionEntities.length > 0 ? (
            <ReferencedEntitiesPills
              entities={textSelectionEntities}
              onRemove={removeSelection}
              className="px-0 py-0"
            />
          ) : null
        }
      />
    </FloatingChatBody>
  );
}

/** Floating workspace chat — header, session chrome, and chat body. */
export function WorkspaceFloatingChatPane({
  flowSessionId,
  scopeWorkspaceId,
  sessions,
  canGoBack,
  onGoBack,
  onClose,
  isChatExpanded,
  onToggleExpand,
  onOpenSession,
  onNewChat,
  onOpenAsTab,
}: WorkspaceFloatingChatPaneProps) {
  const queryClient = useQueryClient();
  const deleteFlowSession = useDeleteFlowSession();

  const { data: currentSession } = useFlowSessionById(flowSessionId ?? "", {
    enabled: !!flowSessionId,
  });

  const handleSwitchSession = useCallback(
    (sessionId: string) => {
      onOpenSession(sessionId, "replace");
    },
    [onOpenSession],
  );

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      try {
        await deleteFlowSession.mutateAsync(sessionId);
        queryClient.invalidateQueries({
          queryKey: ["workspace-flow-sessions"],
        });

        if (sessionId === flowSessionId) {
          const remaining = sessions.filter(
            (s) => s.flow_session_id !== sessionId,
          );
          if (remaining.length > 0) {
            onOpenSession(remaining[0]!.flow_session_id, "replace");
          } else {
            onClose();
          }
        }
      } catch (error) {
        toast.error("Failed to delete chat");
        Sentry.captureException(
          error instanceof Error ? error : new Error("Failed to delete chat"),
          {
            tags: {
              component: "WorkspaceFloatingChatPane",
              operation: "deleteSession",
            },
            extra: { sessionId },
          },
        );
      }
    },
    [
      deleteFlowSession,
      queryClient,
      flowSessionId,
      sessions,
      onOpenSession,
      onClose,
    ],
  );

  const header = (
    <FloatingChatHeader
      title={currentSession?.name || "New chat"}
      updatedAt={currentSession?.updated_at}
      sessions={sessions.map((item) => ({
        key: item.id,
        id: item.flow_session_id,
        name: item.flow_session?.name || "Untitled chat",
        updatedAt: item.updated_at,
      }))}
      currentSessionId={flowSessionId}
      onSwitchSession={handleSwitchSession}
      onDeleteSession={handleDeleteSession}
      deleteSessionAriaLabel="Delete chat"
      onNewChat={onNewChat}
      canGoBack={canGoBack}
      onGoBack={onGoBack}
      isChatExpanded={isChatExpanded}
      onToggleExpand={onToggleExpand}
      onClose={onClose}
      extraActions={
        /* Promote this conversation to a full-page tab. */
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onOpenAsTab}
                disabled={!flowSessionId}
                aria-label="Open as tab"
              >
                <ExternalLink />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Open as tab</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      }
    />
  );

  return (
    /* In-app drag target wraps the WHOLE window (header included) and marks
       itself as occluding: the window floats over the page's own droppables
       (folder cards, the hero composer), which would otherwise win collision
       as the smaller rects. Sits above the keyed DropZoneProvider so a
       session switch mid-drag doesn't unmount the droppable. */
    <ComposerAttachDropTarget
      scopeKey={floatingChatScopeKey(scopeWorkspaceId)}
      occludes
      className="h-full min-h-0"
    >
      {/* Keyed by session so switching gives a clean per-session ChatArea /
          composer. A draft keeps key="draft" until its first send commits an id,
          so attaching a file in a draft doesn't remount and drop the queue. */}
      <FloatingChatPaneShell
        header={header}
        sessionKey={flowSessionId ?? "draft"}
        initialSessionId={flowSessionId ?? undefined}
      >
        <WorkspaceFloatingChatBody
          flowSessionId={flowSessionId}
          scopeWorkspaceId={scopeWorkspaceId}
          onCommitSession={(id) => onOpenSession(id, "replace")}
        />
      </FloatingChatPaneShell>
    </ComposerAttachDropTarget>
  );
}
